import React, { useEffect, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useMetricsUi } from "./metricsUi";
import { isMobile } from "../../globalStyles/mobile";

const SLOW_LOAD_MS = 4000;

/**
 * Page-level loading layout shared by dashboard analysis pages.
 * Visual placeholders only — no metrics, empty, or error copy.
 * The optional section flags / `KpiGridComponent` let a configurable page
 * draw only the sections it will show; the defaults keep the original layout.
 */
const SalesPerformancePageLoading = ({
  title = "Loading sales analysis...",
  kpiCount = 6,
  showChart = true,
  showTable = true,
  KpiGridComponent = null,
}) => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const kpiTiles = Array.from({ length: kpiCount }).map((_, index) => (
    <Box
      key={index}
      data-testid="loading-kpi"
      sx={{
        ...ui.card,
        padding: "14px 16px 13px",
        minHeight: 104,
      }}
    >
      <Skeleton variant="text" width="46%" height={16} />
      <Skeleton variant="text" width="68%" height={32} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="38%" height={14} sx={{ mt: 1 }} />
    </Box>
  ));

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {title}
        </Typography>
        {slow && (
          <Typography
            sx={{
              fontSize: 12.5,
              color: colors.textTertiary,
              mt: 0.5,
            }}
          >
            This may take a few moments.
          </Typography>
        )}
      </Box>

      {kpiCount > 0 &&
        (KpiGridComponent ? (
          <KpiGridComponent>{kpiTiles}</KpiGridComponent>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile()
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(190px, 1fr))",
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {kpiTiles}
          </Box>
        ))}

      {showChart && (
        <Box
          data-testid="loading-chart"
          sx={{ ...ui.card, px: 2.25, pt: 1.75, pb: 2, mb: 3 }}
        >
          <Skeleton variant="text" width="42%" height={20} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={220} />
        </Box>
      )}

      {showTable && (
        <Box data-testid="loading-table" sx={{ ...ui.card, px: 2.25, py: 2 }}>
          <Skeleton variant="text" width="28%" height={18} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={16} />
        </Box>
      )}
    </Box>
  );
};

export default SalesPerformancePageLoading;
